import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { adminApiService } from '../../services/adminApi';
import { formatDateTimeUTC } from '../../utils/dateTime';
import ConvertedTokensChart from '../converter/ConvertedTokensChart';
import UsageBreakdownChart from '../converter/UsageBreakdownChart';
import { CliCost, tokensForOperation } from '../converter/cliCost';
import { convertedTokensByDay, KindUsageRow, TokenKind } from '../converter/usageBreakdown';
import DateRangeFilter from '../filters/DateRangeFilter';
import { idsParam } from '../filters/EntityFilter';
import { SectionFilterBar, TableCard, TableHeader, TableViewport, usePageSize } from '../table/TableKit';
import RefreshButton from '../RefreshButton';
import { usePageClamp } from '../../hooks/usePageClamp';
import { useUsageRange } from '../../hooks/useUsageRange';
import { fromHere } from '../../hooks/useBackTo';
import { useSearchDraft } from '../../hooks/useSearchDraft';
import { urlEnum, urlIdList, urlIsoDate, urlOptionalId, urlPositiveInt, urlString, useUrlState } from '../../hooks/useUrlState';
import {
  ApiKeySelect,
  Badge,
  EmptyRow,
  ErrorNotice,
  InstanceSelect,
  SortableTh,
  StatTile,
  formatNumber,
  instanceLabel,
  selectClass,
  tdClass,
  thClass,
} from './shared';
import { apiKeyName, maskApiKey } from '../../utils/apiKey';
import { formatCentsAsDollars } from '../../utils/money';

type Scope = 'cli' | 'api' | 'web';

const SCOPE_LABELS: Record<Scope, string> = { cli: 'CLI', api: 'API', web: 'WEB' };

const CHART_TOKEN_KINDS: TokenKind[] = ['converted', 'supported', 'input', 'output'];
// The API only converts, so supported tokens are always 0.
const API_CHART_TOKEN_KINDS: TokenKind[] = ['converted', 'input', 'output'];

const API_SCOPE = 'api:convert';
const CLI_SCOPE = 'cli:analyze,cli:convert';

const USAGE_URL_SPEC = {
  tab: urlEnum(['cli', 'api', 'web'] as const, 'cli'),
};

const API_URL_SPEC = {
  instance: urlOptionalId(),
  keys: urlIdList(),
  status: urlEnum(['', 'success', 'error'] as const, ''),
  range: urlEnum(['7d', '30d', 'custom'] as const, '30d'),
  from: urlIsoDate(),
  to: urlIsoDate(),
  q: urlString(),
  sort: urlEnum(['status', 'instance_name', 'api_key', 'input_tokens', 'input_tokens_converted', 'output_tokens', 'cost', 'completed_at'] as const, 'completed_at'),
  order: urlEnum(['asc', 'desc'] as const, 'desc'),
  page: urlPositiveInt(),
};

const CLI_URL_SPEC = {
  instance: urlOptionalId(),
  keys: urlIdList(),
  operation: urlEnum(['', 'analyze', 'convert'] as const, ''),
  status: urlEnum(['', 'authorized', 'success', 'failed', 'expired'] as const, ''),
  version: urlString(),
  range: urlEnum(['7d', '30d', 'custom'] as const, '30d'),
  from: urlIsoDate(),
  to: urlIsoDate(),
  q: urlString(),
  sort: urlEnum(['status', 'operation', 'instance_name', 'project_name', 'api_key', 'file_count', 'input_tokens', 'input_tokens_converted', 'input_tokens_supported', 'output_tokens', 'cost', 'version', 'authorized_at'] as const, 'authorized_at'),
  order: urlEnum(['asc', 'desc'] as const, 'desc'),
  page: urlPositiveInt(),
};

const WEB_URL_SPEC = {
  instance: urlOptionalId(),
  operation: urlEnum(['', 'analyze', 'convert'] as const, ''),
  range: urlEnum(['7d', '30d', 'custom'] as const, '30d'),
  from: urlIsoDate(),
  to: urlIsoDate(),
  q: urlString(),
  sort: urlEnum(['instance_name', 'project_name', 'operation', 'input_tokens', 'input_tokens_converted', 'input_tokens_supported', 'output_tokens', 'cost', 'completed_at'] as const, 'completed_at'),
  order: urlEnum(['asc', 'desc'] as const, 'desc'),
  page: urlPositiveInt(),
};

function adminKeyLabel(
  day: { api_key_id: number; api_key_prefix: string; api_key_format: string; name: string; instance_name: string; instance_key: string },
  instanceId: number | undefined,
): string {
  const base = day.name || maskApiKey(day);
  if (day.api_key_id === 0 || instanceId) return base;
  return `${base} · ${day.instance_name || day.instance_key}`;
}

function ApiUsageTab({ urlPrefix }: { urlPrefix: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [pageSize, setPageSize] = usePageSize('admin-api-usage');
  const [state, setState] = useUrlState(API_URL_SPEC, urlPrefix);
  const [searchInput, setSearchInput] = useSearchDraft(state.q, (q) => setState({ q, page: 1 }));
  const { rangeFilter, range } = useUsageRange(state.range, state.from, state.to);
  const apiKeyIdsParam = idsParam(state.keys);
  const offset = (state.page - 1) * pageSize;
  const onSort = (sort: string, order: 'asc' | 'desc') => setState({
    sort: sort as typeof state.sort,
    order,
    page: 1,
  });

  const params = useMemo(
    () => ({
      limit: pageSize,
      offset,
      instance_id: state.instance,
      api_key_ids: apiKeyIdsParam,
      status: state.status || undefined,
      from: range?.from,
      to: range?.to,
      search: state.q || undefined,
      sort: state.sort,
      order: state.order,
    }),
    [pageSize, offset, state.instance, apiKeyIdsParam, state.status, range, state.q, state.sort, state.order],
  );

  const { data: fetched, isFetching, isLoading, isPlaceholderData, error, refetch } = useQuery({
    queryKey: ['admin', 'apiUsage', params],
    queryFn: () => adminApiService.getApiUsage(params),
    placeholderData: (prev) => prev,
    retry: false,
    enabled: range !== null,
    refetchOnWindowFocus: true,
  });
  // An incomplete custom range fetches nothing. Drop the old numbers: kept on
  // screen they would read as this range's.
  const data = range ? fetched : undefined;

  usePageClamp({
    page: state.page,
    pageSize,
    total: data?.total,
    ready: !isLoading && !isPlaceholderData && !!data,
    setPage: (page) => setState({ page }),
  });

  const dailyParams = useMemo(
    () => ({
      instance_id: state.instance,
      api_key_ids: apiKeyIdsParam,
      status: state.status || undefined,
      from: range?.from,
      to: range?.to,
    }),
    [state.instance, apiKeyIdsParam, state.status, range],
  );

  const { data: daily, error: dailyError, isFetching: isDailyFetching, refetch: refetchDaily } = useQuery({
    queryKey: ['admin', 'apiUsageDaily', dailyParams],
    queryFn: () => adminApiService.getApiUsageDaily(dailyParams),
    retry: false,
    enabled: range !== null,
    refetchOnWindowFocus: true,
  });

  const convertedRows = useMemo(() => range ? convertedTokensByDay(daily?.days ?? [], range) : [], [daily?.days, range]);

  const chartRows: KindUsageRow[] = useMemo(
    () =>
      (daily?.days ?? []).map((day) => ({
        date: day.date,
        keyId: day.api_key_id,
        keyLabel: adminKeyLabel(day, state.instance),
        tokensByKind: { input: day.input_tokens, converted: day.input_tokens_converted, supported: day.input_tokens_supported, output: day.output_tokens },
        count: day.requests,
      })),
    [daily, state.instance],
  );

  const aggregates = data?.aggregates;
  const items = data?.items ?? [];

  return (
    <div>
      {error && <ErrorNotice message="Failed to load API usage." />}
      <SectionFilterBar>
        <InstanceSelect
          value={state.instance}
          onChange={(instance) => setState({ instance, keys: [], page: 1 })}
        />
        <ApiKeySelect
          instanceId={state.instance}
          scope={API_SCOPE}
          value={state.keys}
          onChange={(keys) => setState({ keys, page: 1 })}
        />
        <select
          aria-label="Status filter"
          className={selectClass}
          value={state.status}
          onChange={(event) => setState({ status: event.target.value as typeof state.status, page: 1 })}
        >
          <option value="">All Statuses</option>
          <option value="success">success</option>
          <option value="error">error</option>
        </select>
        <DateRangeFilter
          value={rangeFilter}
          onChange={(next) => setState({ range: next.preset, from: next.from, to: next.to, page: 1 })}
        />
        <RefreshButton
          onClick={() => {
            refetch();
            refetchDaily();
          }}
          fetching={isFetching || isDailyFetching}
          disabled={!range}
        />
      </SectionFilterBar>
      <div role="group" aria-label="Usage totals" className="grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-4 mb-6">
        <StatTile label="Requests" value={formatNumber(aggregates?.total_requests ?? 0)} />
        <StatTile label="Success" value={formatNumber(aggregates?.successful ?? 0)} />
        <StatTile label="Error" value={formatNumber(aggregates?.failed ?? 0)} />
        <StatTile label="Unique Entries" value={formatNumber(aggregates?.unique_count ?? 0)} />
        <StatTile label="Duplicate Entries" value={formatNumber(aggregates?.duplicate_count ?? 0)} />
        <StatTile label="In Tokens" value={formatNumber(aggregates?.input_tokens_sum ?? 0)} />
        <StatTile label="In Tokens (Converted)" value={formatNumber(aggregates?.input_tokens_converted_sum ?? 0)} />
        <StatTile label="Out Tokens" value={formatNumber(aggregates?.output_tokens_sum ?? 0)} />
      </div>
      <UsageBreakdownChart rows={chartRows} range={range} countLabel="Requests" tokenKinds={API_CHART_TOKEN_KINDS} error={!!dailyError} urlPrefix={urlPrefix} />
      <ConvertedTokensChart rows={convertedRows} range={range} error={!!dailyError} />
      <TableCard>
        <TableHeader
          title="API Key Requests"
          search={{
            value: searchInput,
            onChange: setSearchInput,
            ariaLabel: 'Search API key requests',
          }}
          pagination={{
            offset,
            limit: pageSize,
            total: data?.total ?? 0,
            onOffsetChange: (nextOffset) => setState({ page: nextOffset / pageSize + 1 }),
            onLimitChange: (size) => {
              setPageSize(size);
              setState({ page: 1 });
            },
          }}
        />
        <TableViewport className={isLoading ? 'opacity-50 transition-opacity' : ''}>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <SortableTh label="Status" sortKey="status" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="Instance" sortKey="instance_name" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="API Key" sortKey="api_key" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="In Tokens" sortKey="input_tokens" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="In Tokens (Converted)" sortKey="input_tokens_converted" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="Out Tokens" sortKey="output_tokens" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="Cost" sortKey="cost" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="Completed At" sortKey="completed_at" sort={state.sort} order={state.order} onSort={onSort} />
                <th className={thClass}>Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="cursor-pointer hover:bg-body-bg/50"
                  onClick={() => navigate(`/admin/usage/api/requests/${item.id}`, { state: fromHere(location) })}
                >
                  <td className={tdClass}><Badge value={item.status} /></td>
                  <td className={tdClass}>{instanceLabel(item.instance_name, item.instance_key)}</td>
                  <td className={tdClass}>{apiKeyName(item)}</td>
                  <td className={tdClass}>{formatNumber(item.input_tokens_sum)}</td>
                  <td className={tdClass}>{formatNumber(item.input_tokens_converted_sum)}</td>
                  <td className={tdClass}>{formatNumber(item.output_tokens_sum)}</td>
                  <td className={tdClass}>{formatCentsAsDollars(item.cost_cents)}</td>
                  <td className={tdClass}>{formatDateTimeUTC(item.completed_at)}</td>
                  <td className={`${tdClass} max-w-xs truncate`} title={item.error_message ?? ''}>
                    {item.error_message || '—'}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <EmptyRow colSpan={9} text={range ? 'No API requests found.' : 'Pick a valid date range.'} />
              )}
            </tbody>
          </table>
        </TableViewport>
      </TableCard>
    </div>
  );
}

function CliUsageTab({ urlPrefix }: { urlPrefix: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [pageSize, setPageSize] = usePageSize('admin-cli-usage');
  const [state, setState] = useUrlState(CLI_URL_SPEC, urlPrefix);
  const [searchInput, setSearchInput] = useSearchDraft(state.q, (q) => setState({ q, page: 1 }));
  const { rangeFilter, range } = useUsageRange(state.range, state.from, state.to);
  const apiKeyIdsParam = idsParam(state.keys);
  const offset = (state.page - 1) * pageSize;
  const onSort = (sort: string, order: 'asc' | 'desc') => setState({
    sort: sort as typeof state.sort,
    order,
    page: 1,
  });

  const params = useMemo(
    () => ({
      limit: pageSize,
      offset,
      instance_id: state.instance,
      api_key_ids: apiKeyIdsParam,
      operation: state.operation || undefined,
      status: state.status || undefined,
      version: state.version || undefined,
      from: range?.from,
      to: range?.to,
      search: state.q || undefined,
      sort: state.sort,
      order: state.order,
    }),
    [pageSize, offset, state.instance, apiKeyIdsParam, state.operation, state.status, state.version, range, state.q, state.sort, state.order],
  );

  const { data: fetched, isFetching, isLoading, isPlaceholderData, error, refetch } = useQuery({
    queryKey: ['admin', 'cliOperations', params],
    queryFn: () => adminApiService.getCliOperations(params),
    placeholderData: (prev) => prev,
    retry: false,
    enabled: range !== null,
    refetchOnWindowFocus: true,
  });
  // An incomplete custom range fetches nothing. Drop the old numbers: kept on
  // screen they would read as this range's.
  const data = range ? fetched : undefined;

  usePageClamp({
    page: state.page,
    pageSize,
    total: data?.total,
    ready: !isLoading && !isPlaceholderData && !!data,
    setPage: (page) => setState({ page }),
  });

  const dailyParams = useMemo(
    () => ({
      instance_id: state.instance,
      api_key_ids: apiKeyIdsParam,
      operation: state.operation || undefined,
      status: state.status || undefined,
      version: state.version || undefined,
      from: range?.from,
      to: range?.to,
    }),
    [state.instance, apiKeyIdsParam, state.operation, state.status, state.version, range],
  );

  const { data: daily, error: dailyError, isFetching: isDailyFetching, refetch: refetchDaily } = useQuery({
    queryKey: ['admin', 'cliUsageDaily', dailyParams],
    queryFn: () => adminApiService.getCliUsageDaily(dailyParams),
    retry: false,
    enabled: range !== null,
    refetchOnWindowFocus: true,
  });

  const convertedRows = useMemo(() => range ? convertedTokensByDay(daily?.days ?? [], range) : [], [daily?.days, range]);

  const chartRows: KindUsageRow[] = useMemo(
    () =>
      (daily?.days ?? []).map((day) => ({
        date: day.date,
        keyId: day.api_key_id,
        keyLabel: adminKeyLabel(day, state.instance),
        tokensByKind: { input: day.input_tokens, converted: day.input_tokens_converted, supported: day.input_tokens_supported, output: day.output_tokens },
        count: day.operations,
      })),
    [daily, state.instance],
  );

  const aggregates = data?.aggregates;

  return (
    <div>
      {error && <ErrorNotice message="Failed to load CLI usage." />}
      <SectionFilterBar>
        <InstanceSelect
          value={state.instance}
          onChange={(instance) => setState({ instance, keys: [], page: 1 })}
        />
        <ApiKeySelect
          instanceId={state.instance}
          scope={CLI_SCOPE}
          value={state.keys}
          onChange={(keys) => setState({ keys, page: 1 })}
        />
        <select
          aria-label="Operation filter"
          className={selectClass}
          value={state.operation}
          onChange={(event) => setState({ operation: event.target.value as typeof state.operation, page: 1 })}
        >
          <option value="">All Operations</option>
          <option value="analyze">analyze</option>
          <option value="convert">convert</option>
        </select>
        <select
          aria-label="Status filter"
          className={selectClass}
          value={state.status}
          onChange={(event) => setState({ status: event.target.value as typeof state.status, page: 1 })}
        >
          <option value="">All Statuses</option>
          <option value="authorized">authorized</option>
          <option value="success">success</option>
          <option value="failed">failed</option>
          <option value="expired">expired</option>
        </select>
        <select
          aria-label="Version filter"
          className={selectClass}
          value={state.version}
          onChange={(event) => setState({ version: event.target.value, page: 1 })}
        >
          <option value="">All Versions</option>
          {(data?.versions ?? []).map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </select>
        <DateRangeFilter
          value={rangeFilter}
          onChange={(next) => setState({ range: next.preset, from: next.from, to: next.to, page: 1 })}
        />
        <RefreshButton
          onClick={() => {
            refetch();
            refetchDaily();
          }}
          fetching={isFetching || isDailyFetching}
          disabled={!range}
        />
      </SectionFilterBar>
      <div role="group" aria-label="Usage totals" className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <StatTile label="Operations" value={formatNumber(data?.total ?? 0)} />
        <StatTile label="Unique Entries" value={formatNumber(aggregates?.unique_count ?? 0)} />
        <StatTile label="Duplicate Entries" value={formatNumber(aggregates?.duplicate_count ?? 0)} />
        <StatTile label="In Tokens" value={formatNumber(aggregates?.input_tokens_sum ?? 0)} />
        <StatTile label="In Tokens (Converted)" value={formatNumber(aggregates?.input_tokens_converted_sum ?? 0)} />
        <StatTile label="In Tokens (Supported)" value={formatNumber(aggregates?.input_tokens_supported_sum ?? 0)} />
        <StatTile label="Out Tokens" value={formatNumber(aggregates?.output_tokens_sum ?? 0)} />
      </div>
      <UsageBreakdownChart rows={chartRows} range={range} countLabel="Operations" tokenKinds={CHART_TOKEN_KINDS} error={!!dailyError} urlPrefix={urlPrefix} />
      <ConvertedTokensChart rows={convertedRows} range={range} error={!!dailyError} />
      <TableCard>
        <TableHeader
          title="Operations"
          search={{
            value: searchInput,
            onChange: setSearchInput,
            ariaLabel: 'Search operations',
          }}
          pagination={{
            offset,
            limit: pageSize,
            total: data?.total ?? 0,
            onOffsetChange: (nextOffset) => setState({ page: nextOffset / pageSize + 1 }),
            onLimitChange: (size) => {
              setPageSize(size);
              setState({ page: 1 });
            },
          }}
        />
        <TableViewport className={isLoading ? 'opacity-50 transition-opacity' : ''}>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <SortableTh label="Status" sortKey="status" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="Operation" sortKey="operation" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="Instance" sortKey="instance_name" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="Project" sortKey="project_name" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="API Key" sortKey="api_key" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="Files" sortKey="file_count" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="In Tokens" sortKey="input_tokens" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="In Tokens (Converted)" sortKey="input_tokens_converted" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="In Tokens (Supported)" sortKey="input_tokens_supported" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="Out Tokens" sortKey="output_tokens" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="Cost" sortKey="cost" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="Version" sortKey="version" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="Authorized At" sortKey="authorized_at" sort={state.sort} order={state.order} onSort={onSort} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {(data?.items ?? []).map((item) => (
                <tr
                  key={item.id}
                  className="cursor-pointer hover:bg-body-bg/50"
                  onClick={() => navigate(`/admin/usage/cli/operations/${item.id}`, { state: fromHere(location) })}
                >
                  <td className="px-4 py-3 text-sm"><Badge value={item.status} /></td>
                  <td className={tdClass}>{item.operation}</td>
                  <td className={tdClass}>{instanceLabel(item.instance_name, item.instance_key)}</td>
                  <td className={tdClass}>{item.project_name}</td>
                  <td className={tdClass}>{apiKeyName(item)}</td>
                  <td className={tdClass}>{item.file_count ?? '—'}</td>
                  <td className={tdClass}>{formatNumber(item.input_tokens_sum)}</td>
                  <td className={tdClass}>{tokensForOperation(item.operation, 'converted', item.input_tokens_converted_sum)}</td>
                  <td className={tdClass}>{tokensForOperation(item.operation, 'supported', item.input_tokens_supported_sum)}</td>
                  <td className={tdClass}>{formatNumber(item.output_tokens_sum)}</td>
                  <td className={tdClass}><CliCost operation={item.operation} cost={item.cost_cents} estimate={item.estimated_cost_cents} /></td>
                  <td className={tdClass}>{item.alchemist_version}</td>
                  <td className={tdClass}>{formatDateTimeUTC(item.authorized_at)}</td>
                </tr>
              ))}
              {(data?.items ?? []).length === 0 && (
                <EmptyRow colSpan={13} text={range ? 'No CLI operations found.' : 'Pick a valid date range.'} />
              )}
            </tbody>
          </table>
        </TableViewport>
      </TableCard>
    </div>
  );
}

function WebUsageTab({ urlPrefix }: { urlPrefix: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [pageSize, setPageSize] = usePageSize('admin-web-usage');
  const [state, setState] = useUrlState(WEB_URL_SPEC, urlPrefix);
  const [searchInput, setSearchInput] = useSearchDraft(state.q, (q) => setState({ q, page: 1 }));
  const { rangeFilter, range } = useUsageRange(state.range, state.from, state.to);
  const offset = (state.page - 1) * pageSize;
  const onSort = (sort: string, order: 'asc' | 'desc') => setState({
    sort: sort as typeof state.sort,
    order,
    page: 1,
  });

  const params = useMemo(
    () => ({
      limit: pageSize,
      offset,
      instance_id: state.instance,
      operation: state.operation || undefined,
      from: range?.from,
      to: range?.to,
      search: state.q || undefined,
      sort: state.sort,
      order: state.order,
    }),
    [pageSize, offset, state.instance, state.operation, range, state.q, state.sort, state.order],
  );

  const { data: fetched, isFetching, isLoading, isPlaceholderData, error, refetch } = useQuery({
    queryKey: ['admin', 'webUsage', params],
    queryFn: () => adminApiService.getWebUsage(params),
    placeholderData: (prev) => prev,
    retry: false,
    enabled: range !== null,
    refetchOnWindowFocus: true,
  });
  // An incomplete custom range fetches nothing. Drop the old numbers: kept on
  // screen they would read as this range's.
  const data = range ? fetched : undefined;

  usePageClamp({
    page: state.page,
    pageSize,
    total: data?.total,
    ready: !isLoading && !isPlaceholderData && !!data,
    setPage: (page) => setState({ page }),
  });

  const dailyParams = useMemo(
    () => ({
      instance_id: state.instance,
      operation: state.operation || undefined,
      from: range?.from,
      to: range?.to,
    }),
    [state.instance, state.operation, range],
  );

  const { data: daily, error: dailyError, isFetching: isDailyFetching, refetch: refetchDaily } = useQuery({
    queryKey: ['admin', 'webUsageDaily', dailyParams],
    queryFn: () => adminApiService.getWebUsageDaily(dailyParams),
    retry: false,
    enabled: range !== null,
    refetchOnWindowFocus: true,
  });

  const convertedRows = useMemo(() => range ? convertedTokensByDay(daily?.days ?? [], range) : [], [daily?.days, range]);

  // One series per instance. The chart sums the operation rows of a day.
  // instance_id 0 is the server "Other" bucket.
  const chartRows: KindUsageRow[] = useMemo(
    () =>
      (daily?.days ?? []).map((day) => ({
        date: day.date,
        keyId: day.instance_id,
        keyLabel: day.instance_name,
        tokensByKind: { input: day.input_tokens, converted: day.input_tokens_converted, supported: day.input_tokens_supported, output: day.output_tokens },
        count: day.jobs,
      })),
    [daily],
  );

  const aggregates = data?.aggregates;
  const items = data?.items ?? [];

  return (
    <div>
      {error && <ErrorNotice message="Failed to load WEB usage." />}
      <SectionFilterBar>
        <InstanceSelect
          value={state.instance}
          onChange={(instance) => setState({ instance, page: 1 })}
        />
        <select
          aria-label="Operation filter"
          className={selectClass}
          value={state.operation}
          onChange={(event) => setState({ operation: event.target.value as typeof state.operation, page: 1 })}
        >
          <option value="">All Operations</option>
          <option value="analyze">analyze</option>
          <option value="convert">convert</option>
        </select>
        <DateRangeFilter
          value={rangeFilter}
          onChange={(next) => setState({ range: next.preset, from: next.from, to: next.to, page: 1 })}
        />
        <RefreshButton
          onClick={() => {
            refetch();
            refetchDaily();
          }}
          fetching={isFetching || isDailyFetching}
          disabled={!range}
        />
      </SectionFilterBar>
      <div role="group" aria-label="Usage totals" className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <StatTile label="Jobs" value={formatNumber(aggregates?.total_jobs ?? 0)} />
        <StatTile label="Unique Entries" value={formatNumber(aggregates?.unique_count ?? 0)} />
        <StatTile label="Duplicate Entries" value={formatNumber(aggregates?.duplicate_count ?? 0)} />
        <StatTile label="In Tokens" value={formatNumber(aggregates?.input_tokens_sum ?? 0)} />
        <StatTile label="In Tokens (Converted)" value={formatNumber(aggregates?.input_tokens_converted_sum ?? 0)} />
        <StatTile label="In Tokens (Supported)" value={formatNumber(aggregates?.input_tokens_supported_sum ?? 0)} />
        <StatTile label="Out Tokens" value={formatNumber(aggregates?.output_tokens_sum ?? 0)} />
      </div>
      <UsageBreakdownChart rows={chartRows} range={range} countLabel="Jobs" tokenKinds={CHART_TOKEN_KINDS} error={!!dailyError} urlPrefix={urlPrefix} />
      <ConvertedTokensChart rows={convertedRows} range={range} error={!!dailyError} />
      <TableCard>
        <TableHeader
          title="Completed Jobs"
          search={{
            value: searchInput,
            onChange: setSearchInput,
            ariaLabel: 'Search web jobs',
          }}
          pagination={{
            offset,
            limit: pageSize,
            total: data?.total ?? 0,
            onOffsetChange: (nextOffset) => setState({ page: nextOffset / pageSize + 1 }),
            onLimitChange: (size) => {
              setPageSize(size);
              setState({ page: 1 });
            },
          }}
        />
        <TableViewport className={isLoading ? 'opacity-50 transition-opacity' : ''}>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <SortableTh label="Instance" sortKey="instance_name" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="Project" sortKey="project_name" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="Operation" sortKey="operation" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="In Tokens" sortKey="input_tokens" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="In Tokens (Converted)" sortKey="input_tokens_converted" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="In Tokens (Supported)" sortKey="input_tokens_supported" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="Out Tokens" sortKey="output_tokens" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="Cost" sortKey="cost" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="Completed At" sortKey="completed_at" sort={state.sort} order={state.order} onSort={onSort} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="cursor-pointer hover:bg-body-bg/50"
                  onClick={() => navigate(`/admin/usage/web/jobs/${item.id}`, { state: fromHere(location) })}
                >
                  <td className={tdClass}>{instanceLabel(item.instance_name, item.instance_key)}</td>
                  <td className={tdClass}>{item.project_name}</td>
                  <td className={tdClass}>{item.operation}</td>
                  <td className={tdClass}>{formatNumber(item.input_tokens_sum)}</td>
                  <td className={tdClass}>{tokensForOperation(item.operation, 'converted', item.input_tokens_converted_sum)}</td>
                  <td className={tdClass}>{tokensForOperation(item.operation, 'supported', item.input_tokens_supported_sum)}</td>
                  <td className={tdClass}>{formatNumber(item.output_tokens_sum)}</td>
                  <td className={tdClass}><CliCost operation={item.operation} cost={item.cost_cents} estimate={item.estimated_cost_cents} /></td>
                  <td className={tdClass}>{formatDateTimeUTC(item.completed_at)}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <EmptyRow colSpan={9} text={range ? 'No web jobs found.' : 'Pick a valid date range.'} />
              )}
            </tbody>
          </table>
        </TableViewport>
      </TableCard>
    </div>
  );
}

function AdminUsage() {
  const [{ tab }, setUrlState] = useUrlState(USAGE_URL_SPEC);

  return (
    <div>
      <h2 className="text-xl font-bold text-heading mb-4">Usage Analytics</h2>
      <div className="border-b border-gray-700 mb-6">
        <nav className="flex space-x-8">
          {(['cli', 'api', 'web'] as Scope[]).map((item) => (
            <button
              key={item}
              onClick={() => setUrlState({ tab: item })}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                tab === item
                  ? 'border-primary text-primary'
                  : 'border-transparent text-secondary hover:text-body-color'
              }`}
            >
              {SCOPE_LABELS[item]}
            </button>
          ))}
        </nav>
      </div>
      <p className="text-xs text-secondary mb-3">All dates and times are UTC.</p>
      {tab === 'cli' && <CliUsageTab urlPrefix="cli" />}
      {tab === 'api' && <ApiUsageTab urlPrefix="api" />}
      {tab === 'web' && <WebUsageTab urlPrefix="web" />}
    </div>
  );
}

export default AdminUsage;
